import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { api, ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ChevronLeft, Lock } from "lucide-react";
import { Countdown } from "@/components/Countdown";
import { PlayerCombobox } from "@/components/PlayerCombobox";
import { loadGroupTeams, getWMStart, isBonusLocked, BONUS_MAX_POINTS, AT_PROGRESSION_OPTIONS, type GroupTeam } from "@/lib/bonus";
import { teamDe } from "@/lib/teamNames";
import type { AtProgression } from "@/api/client";

// Fold-tolerant compare for player names (case/accents/punctuation): the
// players table drifted between "M. Arnautović" and "M. Arnautovic", so an
// exact compare zeroes tips that are actually right. Team names are compared
// via teamDe() so an English DB value ("Spain") matches a German admin truth
// ("Spanien"). Keep foldName in sync with server/services/bonusScoring.ts —
// the client must render the same per-question verdicts the server scores.
const foldName = (s: string | null | undefined): string =>
  (s ?? "").normalize("NFKD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]/g, "");
const playerEq = (a: string | null | undefined, b: string | null | undefined) => {
  const fb = foldName(b);
  return fb !== "" && foldName(a) === fb;
};

// Resolved bonus truth as returned by /api/bonus/truth. All fields optional
// (null before the admin resolves the corresponding question).
type BonusTruthView = {
  worldChampion?: string | null;
  topScorer?: string | null;
  groupWinners?: Record<string, string> | null;
  atBetterThanDe?: boolean | null;
  atGroupPosition?: number | null;
  atFirstGoalScorer?: string | null;
  atTopScorer?: string | null;
  atProgression?: string | null;
  atGoalsTotal?: number | null;
  resolvedAt?: string | null;
};

export default function Bonus() {
  const { user } = useAuth();
  const [worldChamp, setWorldChamp] = useState("");
  const [topScorer, setTopScorer] = useState("");
  const [groupWinners, setGroupWinners] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [points, setPoints] = useState(0);
  const [groupTeams, setGroupTeams] = useState<Record<string, GroupTeam[]>>({});
  const [wmStart, setWmStart] = useState<string | null>(null);
  // Austria-themed extras
  const [atBetterThanDe, setAtBetterThanDe] = useState<"" | "yes" | "no">("");
  const [atGroupPosition, setAtGroupPosition] = useState<"" | "1" | "2" | "3" | "4">("");
  const [atFirstGoalScorer, setAtFirstGoalScorer] = useState("");
  const [atTopScorer, setAtTopScorer] = useState("");
  const [atProgression, setAtProgression] = useState<AtProgression | "">("");
  const [atGoalsTotal, setAtGoalsTotal] = useState<string>("");
  // Server-resolved truth (admin enters via Admin → Bonus tab). When
  // `resolvedAt` is set we render checkmark/cross per question; before
  // that we just show "X/Y getippt" answered counts.
  const [truth, setTruth] = useState<BonusTruthView | null>(null);

  useEffect(() => { document.title = "Deine Bonus-Fragen – Englputzeder Tippspiel"; }, []);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.bonus.me().catch(() => null),
      loadGroupTeams(),
      getWMStart(),
      api.bonus.truth().catch(() => null),
    ]).then(([data, gt, start, tr]) => {
      setTruth(tr);
      if (data) {
        setWorldChamp(data.worldChampion ?? "");
        setTopScorer(data.topScorer ?? "");
        setGroupWinners((data.groupWinners as Record<string, string>) ?? {});
        setPoints(data.pointsEarned ?? 0);
        setAtBetterThanDe(data.atBetterThanDe === true ? "yes" : data.atBetterThanDe === false ? "no" : "");
        setAtGroupPosition(data.atGroupPosition ? String(data.atGroupPosition) as "1"|"2"|"3"|"4" : "");
        setAtFirstGoalScorer(data.atFirstGoalScorer ?? "");
        setAtTopScorer(data.atTopScorer ?? "");
        setAtProgression((data.atProgression as AtProgression) ?? "");
        setAtGoalsTotal(data.atGoalsTotal != null ? String(data.atGoalsTotal) : "");
      }
      setGroupTeams(gt);
      setWmStart(start);
    });
  }, [user]);

  const locked = useMemo(() => isBonusLocked(wmStart), [wmStart]);
  const allChampions = useMemo(() => {
    const set = new Map<string, string>();
    Object.values(groupTeams).flat().forEach(t => set.set(t.name, t.flag));
    return Array.from(set.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [groupTeams]);

  const groupKeys = useMemo(() => Object.keys(groupTeams).sort(), [groupTeams]);

  // Per-section breakdown — answered count + (after resolution) earned pts.
  // Mirrors the server-side scoring rules so the UI never goes out of sync:
  // each match between user's tip and truth = the per-row max for that row.
  const resolved = !!truth?.resolvedAt;
  const breakdown = useMemo(() => {
    const groupKeyCount = Object.keys(groupTeams).length || 12; // default to 12 if not loaded yet
    // WM section
    const wmAnswered = !!worldChamp;
    const wmEarned = resolved && worldChamp && teamDe(truth?.worldChampion) === teamDe(worldChamp) ? 20 : 0;
    // Torschützenkönig
    const tsAnswered = !!topScorer;
    const tsEarned = resolved && topScorer && playerEq(topScorer, truth?.topScorer) ? 15 : 0;
    // Gruppensieger
    const grpAnswered = Object.values(groupWinners).filter(Boolean).length;
    const grpTotal = groupKeyCount;
    let grpEarned = 0;
    let grpCorrect = 0;
    if (resolved) {
      const grpTruth = (truth?.groupWinners ?? {}) as Record<string, string>;
      for (const [k, v] of Object.entries(grpTruth)) {
        if (v && teamDe(groupWinners[k]) === teamDe(v)) { grpEarned += 5; grpCorrect++; }
      }
    }
    // Austria specials — 6 boolean checks
    const atAnsweredFlags = [
      atBetterThanDe !== "",
      atGroupPosition !== "",
      !!atFirstGoalScorer,
      !!atTopScorer,
      atProgression !== "",
      atGoalsTotal !== "",
    ];
    const atAnswered = atAnsweredFlags.filter(Boolean).length;
    let atEarned = 0;
    let atCorrect = 0;
    if (resolved) {
      const goals = atGoalsTotal === "" ? null : Number(atGoalsTotal);
      const pos = atGroupPosition === "" ? null : Number(atGroupPosition);
      const bool = atBetterThanDe === "" ? null : atBetterThanDe === "yes";
      const checks = [
        truth?.atBetterThanDe   != null && bool   != null && truth.atBetterThanDe   === bool,
        truth?.atGroupPosition  != null && pos    != null && truth.atGroupPosition  === pos,
        truth?.atFirstGoalScorer && atFirstGoalScorer && playerEq(atFirstGoalScorer, truth.atFirstGoalScorer),
        truth?.atTopScorer       && atTopScorer       && playerEq(atTopScorer, truth.atTopScorer),
        truth?.atProgression     && atProgression     && truth.atProgression     === atProgression,
        truth?.atGoalsTotal     != null && goals != null && truth.atGoalsTotal     === goals,
      ];
      for (const ok of checks) if (ok) { atEarned += 5; atCorrect++; }
    }
    return {
      wm:    { earned: wmEarned, max: 20, answered: wmAnswered ? 1 : 0, total: 1 },
      ts:    { earned: tsEarned, max: 15, answered: tsAnswered ? 1 : 0, total: 1 },
      grp:   { earned: grpEarned, max: grpTotal * 5, answered: grpAnswered, total: grpTotal, correct: grpCorrect },
      at:    { earned: atEarned, max: 30, answered: atAnswered, total: 6, correct: atCorrect },
      total: { earned: wmEarned + tsEarned + grpEarned + atEarned, max: 20 + 15 + grpTotal * 5 + 30 },
    };
  }, [worldChamp, topScorer, groupWinners, groupTeams, atBetterThanDe, atGroupPosition, atFirstGoalScorer, atTopScorer, atProgression, atGoalsTotal, truth, resolved]);

  const save = async () => {
    if (!user) return;
    if (locked) { toast.error("Bonus-Fragen sind seit WM-Start gesperrt 🔒"); return; }
    setBusy(true);
    try {
      await api.bonus.upsert({
        worldChampion: worldChamp || null,
        topScorer: topScorer || null,
        groupWinners,
        atBetterThanDe: atBetterThanDe === "" ? null : atBetterThanDe === "yes",
        atGroupPosition: atGroupPosition === "" ? null : Number(atGroupPosition),
        atFirstGoalScorer: atFirstGoalScorer || null,
        atTopScorer: atTopScorer || null,
        atProgression: atProgression || null,
        atGoalsTotal: atGoalsTotal === "" ? null : Math.max(0, Math.min(99, Number(atGoalsTotal))),
      });
      if (worldChamp) await api.profiles.update({ bonusWinner: worldChamp });
      toast.success("Bonus-Fragen gespeichert ✅");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler beim Speichern");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Link to="/profil" className="flex items-center gap-1 text-sm text-muted-foreground tap"><ChevronLeft className="h-4 w-4" /> Zurück</Link>

      <div className="bg-hero text-primary-foreground rounded-lg shadow-pop p-5">
        <h1 className="font-display font-extrabold text-2xl">Deine Bonus-Fragen 🌍</h1>
        <p className="text-sm opacity-90 mt-1">Bis zu <b className="underline">+{BONUS_MAX_POINTS} Punkte</b> für dein Tipp-Genie.</p>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <div className="inline-block bg-primary-foreground text-primary rounded-pill px-3 py-1 text-sm font-bold">Aktuell: +{points} Pkt</div>
          {!locked && wmStart && (
            <div className="inline-flex items-center gap-1.5 bg-primary-foreground/15 backdrop-blur rounded-pill px-3 py-1 text-xs">
              ⏳ Sperre in <Countdown target={wmStart} compact />
            </div>
          )}
        </div>
      </div>

      {locked && (
        <div className="rounded-lg p-4 bg-muted border border-border flex items-center gap-3">
          <Lock className="h-5 w-5 text-muted-foreground" />
          <div className="text-sm">
            <div className="font-bold">Bonus-Fragen gesperrt</div>
            <div className="text-xs text-muted-foreground">Die WM hat begonnen — Änderungen sind nicht mehr möglich.</div>
          </div>
        </div>
      )}

      {/* Per-section breakdown. Before resolution: shows answered/total per
          section so user sees where they still need to fill in. After
          admin resolution: shows earned vs max per section + total.
          Stays in sync with the form state — every input change updates
          the breakdown immediately, no save needed. */}
      <div className="bg-card rounded-lg shadow-card p-4 space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="font-display font-bold text-sm">Dein Stand</div>
          {resolved ? (
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-pill px-2 py-0.5">
              Ausgewertet
            </div>
          ) : (
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground bg-muted rounded-pill px-2 py-0.5">
              Vor WM-Start
            </div>
          )}
        </div>

        <BreakdownRow
          emoji="🏆"
          label="Weltmeister"
          earned={breakdown.wm.earned}
          max={breakdown.wm.max}
          answered={breakdown.wm.answered}
          total={breakdown.wm.total}
          resolved={resolved}
        />
        <BreakdownRow
          emoji="⚽"
          label="Torschützenkönig"
          earned={breakdown.ts.earned}
          max={breakdown.ts.max}
          answered={breakdown.ts.answered}
          total={breakdown.ts.total}
          resolved={resolved}
        />
        <BreakdownRow
          emoji="🥇"
          label="Gruppensieger"
          earned={breakdown.grp.earned}
          max={breakdown.grp.max}
          answered={breakdown.grp.answered}
          total={breakdown.grp.total}
          resolved={resolved}
          correctSuffix={resolved ? `· ${breakdown.grp.correct ?? 0}/${breakdown.grp.total} ✓` : undefined}
        />
        <BreakdownRow
          emoji="🇦🇹"
          label="Österreich-Spezial"
          earned={breakdown.at.earned}
          max={breakdown.at.max}
          answered={breakdown.at.answered}
          total={breakdown.at.total}
          resolved={resolved}
          correctSuffix={resolved ? `· ${breakdown.at.correct ?? 0}/${breakdown.at.total} ✓` : undefined}
        />

        <div className="flex items-baseline justify-between pt-2 border-t border-border">
          <span className="text-sm font-bold">Gesamt</span>
          <span className="font-display font-extrabold text-base tabular-nums">
            {resolved ? (
              <>
                <span className="text-accent-foreground bg-accent rounded-pill px-2 py-0.5">
                  {breakdown.total.earned}
                </span>
                <span className="text-muted-foreground"> / {breakdown.total.max} Pkt</span>
              </>
            ) : (
              <span className="text-muted-foreground">bis +{breakdown.total.max} Pkt möglich</span>
            )}
          </span>
        </div>
      </div>

      <div className="bg-card rounded-lg shadow-card p-4 space-y-2">
        <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">🏆 Weltmeister · +20 Pkt</label>
        <select
          value={worldChamp}
          onChange={(e) => setWorldChamp(e.target.value)}
          disabled={locked}
          className="w-full h-12 px-3 rounded-lg border border-input bg-background text-base font-semibold disabled:opacity-50"
        >
          <option value="">— Team wählen —</option>
          {allChampions.map(([name, flag]) => <option key={name} value={name}>{flag} {teamDe(name)}</option>)}
        </select>
      </div>

      <div className="bg-card rounded-lg shadow-card p-4 space-y-2">
        <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">⚽ Torschützenkönig · +15 Pkt</label>
        <PlayerCombobox value={topScorer} onChange={setTopScorer} disabled={locked} />
      </div>

      <div className="bg-card rounded-lg shadow-card p-4 space-y-3">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">🥇 Gruppensieger · je +5 Pkt</div>
        <div className="grid grid-cols-2 gap-2">
          {groupKeys.map(g => (
            <div key={g}>
              <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Gruppe {g}</label>
              <select
                value={groupWinners[g] ?? ""}
                onChange={(e) => setGroupWinners({ ...groupWinners, [g]: e.target.value })}
                disabled={locked}
                className="w-full h-10 px-2 rounded-md border border-input bg-background text-xs disabled:opacity-50"
              >
                <option value="">—</option>
                {(groupTeams[g] ?? []).map(t => <option key={t.name} value={t.name}>{t.flag} {teamDe(t.name)}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>

      {/* Austria-themed Bonus-Fragen — each +5 Pkt on exakt richtig.
          Eigene Section damit sie als „Spezial-Block" erkennbar bleibt. */}
      <div className="bg-card rounded-lg shadow-card p-4 space-y-4 border-2 border-red-600/30">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🇦🇹</span>
          <div>
            <div className="font-display font-bold text-base">Österreich-Spezial</div>
            <div className="text-[11px] text-muted-foreground">6 Fragen · je +5 Pkt · max +30 Pkt</div>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            🆚 Kommt Österreich weiter als Deutschland?
          </label>
          <select
            value={atBetterThanDe}
            onChange={(e) => setAtBetterThanDe(e.target.value as "" | "yes" | "no")}
            disabled={locked}
            className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm font-semibold disabled:opacity-50"
          >
            <option value="">— wählen —</option>
            <option value="yes">✅ Ja, Österreich kommt weiter</option>
            <option value="no">❌ Nein, Deutschland kommt weiter</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            🏅 Welchen Platz belegt Österreich in der Gruppenphase?
          </label>
          <select
            value={atGroupPosition}
            onChange={(e) => setAtGroupPosition(e.target.value as "" | "1" | "2" | "3" | "4")}
            disabled={locked}
            className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm font-semibold disabled:opacity-50"
          >
            <option value="">— wählen —</option>
            <option value="1">1. Platz</option>
            <option value="2">2. Platz</option>
            <option value="3">3. Platz</option>
            <option value="4">4. Platz</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            ⚽ Wer schießt das erste Tor im Turnier für Österreich?
          </label>
          <PlayerCombobox value={atFirstGoalScorer} onChange={setAtFirstGoalScorer} disabled={locked} team="Austria" />
        </div>

        <div className="space-y-2">
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            🎯 Wer wird bester Torschütze von Österreich?
          </label>
          <PlayerCombobox value={atTopScorer} onChange={setAtTopScorer} disabled={locked} team="Austria" />
        </div>

        <div className="space-y-2">
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            🚀 Wie weit kommt Österreich im Turnier?
          </label>
          <select
            value={atProgression}
            onChange={(e) => setAtProgression(e.target.value as AtProgression | "")}
            disabled={locked}
            className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm font-semibold disabled:opacity-50"
          >
            <option value="">— wählen —</option>
            {AT_PROGRESSION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            🥅 Wieviele Tore schießt Österreich gesamt? <span className="font-normal normal-case">(ohne Elfmeterschießen)</span>
          </label>
          <input
            type="number"
            min={0}
            max={99}
            value={atGoalsTotal}
            onChange={(e) => setAtGoalsTotal(e.target.value)}
            disabled={locked}
            placeholder="z. B. 7"
            className="w-full h-11 px-3 rounded-lg border border-input bg-background text-sm font-semibold disabled:opacity-50 tabular-nums"
          />
        </div>
      </div>

      <Button onClick={save} disabled={busy || locked} className="w-full h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-bold">
        {locked ? "🔒 Gesperrt" : busy ? "Speichere …" : "Speichern"}
      </Button>
    </div>
  );
}

/**
 * Single row in the "Dein Stand" breakdown card.
 *
 * Two display modes:
 *   - `resolved=false` (pre-tournament / mid-tournament): "answered/total"
 *      with a progress bar of how many sub-questions the user has filled.
 *   - `resolved=true`  (admin has run resolve): "earned / max Pkt" + a
 *      green progress bar of what they actually scored.
 *
 * Kept inline rather than extracted to a separate file because it has no
 * reuse value outside this page and the props are tightly coupled to the
 * scoring shape.
 */
function BreakdownRow({
  emoji,
  label,
  earned,
  max,
  answered,
  total,
  resolved,
  correctSuffix,
}: {
  emoji: string;
  label: string;
  earned: number;
  max: number;
  answered: number;
  total: number;
  resolved: boolean;
  correctSuffix?: string;
}) {
  const pct = resolved
    ? max > 0 ? Math.round((earned / max) * 100) : 0
    : total > 0 ? Math.round((answered / total) * 100) : 0;
  const barCls = resolved
    ? earned >= max ? "bg-emerald-500" : earned > 0 ? "bg-amber-500" : "bg-muted-foreground/40"
    : answered >= total ? "bg-accent" : "bg-accent/60";
  return (
    <div>
      <div className="flex items-baseline justify-between text-xs">
        <span className="flex items-baseline gap-1.5 min-w-0">
          <span className="text-base leading-none shrink-0">{emoji}</span>
          <span className="font-semibold truncate">{label}</span>
          {!resolved && total > 1 && (
            <span className="text-muted-foreground text-[10px] tabular-nums">
              {answered}/{total} getippt
            </span>
          )}
          {correctSuffix && (
            <span className="text-muted-foreground text-[10px] tabular-nums">{correctSuffix}</span>
          )}
        </span>
        <span className="font-bold tabular-nums shrink-0">
          {resolved ? (
            <>
              <span className={earned > 0 ? "text-emerald-700" : "text-muted-foreground"}>{earned}</span>
              <span className="text-muted-foreground"> / {max} Pkt</span>
            </>
          ) : (
            <span className="text-muted-foreground">bis +{max} Pkt</span>
          )}
        </span>
      </div>
      <div className="h-1 mt-1 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full ${barCls} transition-all`}
          style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
        />
      </div>
    </div>
  );
}
